<template>
	<BaseStep title="Was möchten Sie erreichen?" description="Wählen Sie bis zu 3 Ziele aus.">
		<div class="space-y-3">
			<SelectableCheckboxCard
				v-for="goal in goals"
				:id="goal.id"
				:key="goal.id"
				:title="goal.title"
				:description="goal.description"
				:is-selected="modelValue.includes(goal.id)"
				:data-test-id="`goal-${goal.id}`"
				@toggle="(id: string) => toggleGoal(id)"
			/>
		</div>
		<p v-if="error" class="mt-4 text-sm text-red-600 flex items-center">
			<svg class="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
				<path
					fill-rule="evenodd"
					d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
					clip-rule="evenodd"
				/>
			</svg>
			{{ error }}
		</p>
	</BaseStep>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import BaseStep from './BaseStep.vue';
import SelectableCheckboxCard from '@/components/common/SelectableCheckboxCard.vue';

export interface Goal {
	id: string;
	title: string;
	description: string;
}

interface Props {
	modelValue: string[];
	goals: Goal[];
	maxSelections?: number;
}

const props = withDefaults(defineProps<Props>(), {
	maxSelections: 3,
});

const emit = defineEmits<{
	'update:modelValue': [value: string[]];
	error: [message: string | null];
}>();

const error = ref<string | null>(null);

// Watch for changes to validate max selections
watch(
	() => props.modelValue,
	(newValue) => {
		if (newValue.length > props.maxSelections) {
			error.value = `Bitte wählen Sie maximal ${props.maxSelections} Ziele aus.`;
			emit('error', error.value);
		} else {
			error.value = null;
			emit('error', null);
		}
	},
	{ immediate: true },
);

// Methods
const toggleGoal = (goalId: string) => {
	const newGoals = [...props.modelValue];
	const index = newGoals.indexOf(goalId);

	// If the goal is not already selected and we're at the max, show error
	if (index === -1 && newGoals.length >= props.maxSelections) {
		error.value = `Bitte wählen Sie maximal ${props.maxSelections} Ziele aus.`;
		emit('error', error.value);
		return;
	}

	// Toggle the selected goal
	if (index === -1) {
		newGoals.push(goalId);
	} else {
		newGoals.splice(index, 1);
	}

	// Clear error if we're under the max
	if (newGoals.length <= props.maxSelections) {
		error.value = null;
		emit('error', null);
	}

	emit('update:modelValue', newGoals);
};
</script>
